import cookies from 'js-cookie'
import { useCallback } from 'react'

import {
  ERROR_AUTH_SIGNUP_EMAIL_FAILURE,
  ERROR_SERVER_AUTH_SIGNUP_EMAIL,
  SUCCESS_AUTH_SIGNUP,
} from '@/app/module/auth/definitions'
import { getSignupErrorMessage, setToken } from '@/app/module/auth/helpers'
import { getUserPreferences } from '@/app/module/auth/store/session.slice'
import { createNotification } from '@/app/module/notifications/store/actions'
import { getUser } from '@/app/module/user/store/actions'
import { esApi } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

export type SignupValues = {
  email: string
  password: string
  firstName: string
  lastName: string
  phone: string
  country: string
  organizationType: string
  organization: string
  verifyToken: string
  emailToken: string
  hearAboutUs?: string
}

export const useSignup = () => {
  const dispatch = useAppDispatch()
  const [authSignup, signupRes] = esApi.endpoints.authSignup.useMutation()
  const [checkEmail, emailCheckRes] = esApi.endpoints.authEmailcheck.useLazyQuery()

  const signup = useCallback(
    async (values: SignupValues) => {
      const cid = cookies.get('es_cid')
      const ref = cookies.get('es_referer_url')

      try {
        // First check if email is available
        const { available } = await checkEmail({
          email: values.email,
        }).unwrap()

        if (!available) {
          dispatch(
            createNotification({
              'email-check-exists': { type: 'error', message: ERROR_AUTH_SIGNUP_EMAIL_FAILURE },
            }),
          )
          return false
        }
      } catch {
        dispatch(
          createNotification({ 'email-check-failure': { type: 'error', message: ERROR_SERVER_AUTH_SIGNUP_EMAIL } }),
        )
        return false
      }

      const body = {
        email: values.email,
        password: values.password,
        first_name: values.firstName,
        last_name: values.lastName,
        phone: values.phone,
        country: values.country,
        organization_type: values.organizationType,
        organization: values.organization,
        verify_token: values.verifyToken,
        email_token: values.emailToken,
        marketing_code: cid,
        referer: ref,
        hear_about_us: values.hearAboutUs,
      }

      try {
        const response = await authSignup({ body }).unwrap()

        const authToken = response.access
        setToken(authToken, response.validUntil)

        dispatch(
          createNotification({
            'signup-success': { type: 'success', message: SUCCESS_AUTH_SIGNUP, disableAutoHide: true },
          }),
        )

        const user = await dispatch(getUser({ token: authToken }))
        // bootstrap user preferences before dispatching the success action
        if (user && !(user instanceof Error)) {
          dispatch(getUserPreferences({ user, token: authToken }))
        }

        return user
      } catch (error) {
        dispatch(createNotification({ 'signup-error': { type: 'error', message: getSignupErrorMessage(error) } }))

        return false
      }
    },
    [dispatch, authSignup, checkEmail],
  )

  const isLoading = signupRes.isLoading || emailCheckRes.isLoading || emailCheckRes.isFetching
  const isUninitialized = signupRes.isUninitialized && emailCheckRes.isUninitialized

  return [
    signup,
    {
      ...signupRes,
      isLoading,
      isUninitialized,
    },
  ] as const
}
